import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import TableLayout from '../components/TableLayout';

const GameRoom = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { playerCount, selectedFriends, selectedSubject } = location.state || {};
  const [gameState, setGameState] = useState('waiting'); // waiting, playing, finished
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [timeLeft, setTimeLeft] = useState(15);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [players, setPlayers] = useState([]);
  const [roomCode] = useState('ABC123');

  // Mock questions for the selected subject
  const mockQuestions = {
    'Mathematics': [
      {
        question: "What is 15 + 27?",
        options: ["40", "42", "43", "41"],
        correct: 1
      },
      {
        question: "What is 8 × 7?",
        options: ["54", "56", "58", "52"],
        correct: 1
      }
    ],
    'Science': [
      {
        question: "What is the chemical symbol for gold?",
        options: ["Ag", "Au", "Fe", "Cu"],
        correct: 1
      },
      {
        question: "Which planet is closest to the Sun?",
        options: ["Venus", "Mercury", "Mars", "Earth"],
        correct: 1
      }
    ],
    'English': [
      {
        question: "What is the past tense of 'go'?",
        options: ["goed", "went", "gone", "going"],
        correct: 1
      },
      {
        question: "Which word is a synonym for 'happy'?",
        options: ["sad", "joyful", "angry", "tired"],
        correct: 1
      }
    ],
    'History': [
      {
        question: "In which year did World War II end?",
        options: ["1943", "1944", "1945", "1946"],
        correct: 2
      },
      {
        question: "Who was the first President of India?",
        options: ["Jawaharlal Nehru", "Rajendra Prasad", "Sardar Patel", "Mahatma Gandhi"],
        correct: 1
      }
    ]
  };

  // Initialize players
  useEffect(() => {
    const allPlayers = [
      { id: 1, name: 'You', avatar: '👤', score: 0, isCurrentUser: true },
      ...selectedFriends.map((friend, index) => ({
        id: index + 2,
        name: friend.name,
        avatar: friend.avatar,
        score: 0,
        isCurrentUser: false
      }))
    ];
    setPlayers(allPlayers);
  }, [selectedFriends]);

  // Timer effect
  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      handleTimeUp();
    }
  }, [timeLeft, gameState]);

  const startGame = () => {
    setGameState('playing');
    setTimeLeft(15);
    setCurrentQuestion(mockQuestions[selectedSubject]?.[0] || mockQuestions['Mathematics'][0]);
  };

  const handleAnswerSelect = (answerIndex) => {
    setSelectedAnswer(answerIndex);
    // Simulate other players answering
    setTimeout(() => {
      handleTimeUp();
    }, 1000);
  };

  const handleTimeUp = () => {
    if (selectedAnswer === currentQuestion.correct) {
      // Update score for current user
      setPlayers(prev => prev.map(player => 
        player.isCurrentUser ? { ...player, score: player.score + 10 } : player
      ));
    }
    
    // Move to next question or end game
    const currentIndex = mockQuestions[selectedSubject]?.findIndex(q => q === currentQuestion) || 0;
    const nextQuestion = mockQuestions[selectedSubject]?.[currentIndex + 1];
    
    if (nextQuestion) {
      setCurrentQuestion(nextQuestion);
      setTimeLeft(15);
      setSelectedAnswer(null);
    } else {
      setGameState('finished');
    }
  };

  const getAnswerClass = (answerIndex) => {
    if (selectedAnswer === null) return '';
    if (answerIndex === currentQuestion.correct) return 'bg-green-500 text-white';
    if (answerIndex === selectedAnswer) return 'bg-red-500 text-white';
    return 'bg-gray-200 dark:bg-gray-700';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-200">
              🎮 Quiz Room
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Subject: {selectedSubject} | Room: {roomCode}
            </p>
          </div>
          <button
            onClick={() => navigate('/multiplayer')}
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
          >
            ← Back to Setup
          </button>
        </div>

        <AnimatePresence mode="wait">
          {gameState === 'waiting' && (
            <motion.div
              key="waiting"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className="max-w-4xl mx-auto"
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-6 text-center">
                  Waiting Room
                </h2>
                
                {/* Game Table */}
                <div className="mb-8">
                  <TableLayout 
                    playerCount={playerCount} 
                    layout="game" 
                    selectedFriends={selectedFriends}
                  />
                </div>

                {/* Player List */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  {players.map((player) => (
                    <div key={player.id} className="text-center">
                      <div className="text-3xl mb-2">{player.avatar}</div>
                      <div className="font-semibold text-gray-800 dark:text-gray-200">
                        {player.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Ready
                      </div>
                    </div>
                  ))}
                </div>

                <motion.button
                  onClick={startGame}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-4 px-8 rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Start Quiz
                </motion.button>
              </div>
            </motion.div>
          )}

          {gameState === 'playing' && (
            <motion.div
              key="playing"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className="max-w-4xl mx-auto"
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
                {/* Timer */}
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-red-500 mb-2">
                    {timeLeft}s
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-red-500 h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${(timeLeft / 15) * 100}%` }}
                    ></div>
                  </div>
                </div>

                {/* Question */}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-6">
                    {currentQuestion.question}
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {currentQuestion.options.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => handleAnswerSelect(index)}
                        disabled={selectedAnswer !== null}
                        className={`p-4 rounded-lg border-2 transition-all duration-200 text-left font-medium ${
                          getAnswerClass(index)
                        } ${
                          selectedAnswer === null 
                            ? 'border-gray-200 dark:border-gray-600 hover:border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/10' 
                            : ''
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>

                  {/* End Quiz Button */}
                  <motion.button
                    onClick={() => setGameState('finished')}
                    className="bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    🏁 End Quiz
                  </motion.button>
                </div>

                {/* Player Scores */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {players.map((player) => (
                    <div key={player.id} className="text-center">
                      <div className="text-2xl mb-1">{player.avatar}</div>
                      <div className="font-semibold text-gray-800 dark:text-gray-200">
                        {player.name}
                      </div>
                      <div className="text-lg font-bold text-green-600">
                        {player.score} pts
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {gameState === 'finished' && (
            <motion.div
              key="finished"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className="max-w-4xl mx-auto"
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
                {/* Winner Announcement */}
                {(() => {
                  const sortedPlayers = [...players].sort((a, b) => b.score - a.score);
                  const winner = sortedPlayers[0];
                  const isTie = sortedPlayers.filter(p => p.score === winner.score).length > 1;
                  
                  return (
                    <div className="text-center mb-8">
                      <div className="text-6xl mb-4">🏆</div>
                      <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-2">
                        {isTie ? "It's a Tie!" : "Winner!"}
                      </h2>
                      {!isTie && (
                        <div className="text-xl text-gray-600 dark:text-gray-400 mb-4">
                          Congratulations, <span className="font-bold text-blue-600">{winner.name}</span>!
                        </div>
                      )}
                      <div className="text-lg text-gray-500 dark:text-gray-400">
                        Subject: {selectedSubject} | Total Questions: {mockQuestions[selectedSubject]?.length || 2}
                      </div>
                    </div>
                  );
                })()}
                
                {/* Final Rankings */}
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4 text-center">
                    Final Rankings
                  </h3>
                  <div className="space-y-3">
                    {[...players].sort((a, b) => b.score - a.score).map((player, index) => (
                      <div 
                        key={player.id} 
                        className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                          index === 0 
                            ? 'border-yellow-400 bg-gradient-to-r from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20' 
                            : 'border-gray-200 dark:border-gray-600'
                        }`}
                      >
                        <div className="flex items-center space-x-4">
                          <div className="text-2xl">
                            {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}.`}
                          </div>
                          <div className="text-2xl">{player.avatar}</div>
                          <div className="font-semibold text-gray-800 dark:text-gray-200">
                            {player.name}
                          </div>
                        </div>
                        <div className="text-xl font-bold text-green-600">
                          {player.score} pts
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{players.length}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Players</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {Math.max(...players.map(p => p.score))}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Highest Score</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">
                      {Math.round(players.reduce((sum, p) => sum + p.score, 0) / players.length)}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Average Score</div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4">
                  <motion.button
                    onClick={() => {
                      setGameState('waiting');
                      setPlayers(prev => prev.map(p => ({ ...p, score: 0 })));
                    }}
                    className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    🎮 Play Again
                  </motion.button>
                  
                  <motion.button
                    onClick={() => navigate('/multiplayer')}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-xl transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    ← Back to Setup
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default GameRoom; 